import { POI_CATEGORIES, type PoiCategory, TREE_CONDITIONS, type TreeCondition } from '@api/types'
import { CONDITION_HEX, POI_HEX, ROUTE_HEX, TRACK_HEX } from '@ui/colors'
import { ICON_PATHS, type IconName } from '@ui/Icon'

// Marcadores del mapa, como en el mockup: los árboles son círculos del color de su estado con
// un pino dentro; los puntos de interés, cuadrados redondeados con el icono de su categoría.
// La forma también distingue (el color nunca es la única señal).

// `icon` null: solo la forma (p. ej. el grupo de lugares, que lleva el número encima).
// `outline`: hueco (fondo blanco, aro e icono del color), para lo que aún no es del Camino.
export type MarkerSpec = {
	id: string
	shape: 'circle' | 'square'
	color: string
	icon: IconName | null
	outline?: boolean
}

export const MARKER_SIZE = 32 // px CSS a tamaño 1
export const MARKER_PIXEL_RATIO = 2 // se dibuja al doble para pantallas de alta densidad

export const POI_ICONS: Record<PoiCategory, IconName> = {
	heritage: 'landmark',
	singular_space: 'mountain',
	magic_place: 'sparkles',
}

// Salida y llegada de la etapa elegida: círculos del color de la ruta con ▶ y bandera.
export const STAGE_END_ICONS = { start: 'start', finish: 'flag' } as const satisfies Record<string, IconName>
export const stageEndMarkerId = (end: keyof typeof STAGE_END_ICONS) => `stage-${end}`

export const treeMarkerId = (condition: TreeCondition | 'none') => `tree-${condition}`

// Lugar mágico recibido, pendiente de revisar (solo lo ve el personal): gris, con el mismo icono.
export const MAGIC_PENDING_MARKER: MarkerSpec = {
	id: 'poi-magic-pending',
	shape: 'square',
	color: CONDITION_HEX.none,
	icon: 'sparkles',
}
export const poiMarkerId = (category: PoiCategory) => `poi-${category}`

// Grupo de lugares de interés: el cuadrado azul sin icono; el número lo pone la capa.
export const POI_CLUSTER_MARKER: MarkerSpec = {
	id: 'poi-cluster',
	shape: 'square',
	color: POI_HEX.heritage,
	icon: null,
}

// Variante propuesta: un pueblo que quiere sumarse al Camino. Del color del trazado, pero hueco:
// se relaciona con el Camino sin confundirse con él.
export const VARIANT_MARKER: MarkerSpec = {
	id: 'locality-variant',
	shape: 'circle',
	color: TRACK_HEX,
	icon: 'route',
	outline: true,
}

export const MARKERS: readonly MarkerSpec[] = [
	...[...TREE_CONDITIONS, 'none' as const].map(
		(condition): MarkerSpec => ({
			id: treeMarkerId(condition),
			shape: 'circle',
			color: CONDITION_HEX[condition],
			icon: 'pine',
		})
	),
	...POI_CATEGORIES.map(
		(category): MarkerSpec => ({
			id: poiMarkerId(category),
			shape: 'square',
			color: POI_HEX[category],
			icon: POI_ICONS[category],
		})
	),
	...(['start', 'finish'] as const).map(
		(end): MarkerSpec => ({
			id: stageEndMarkerId(end),
			shape: 'circle',
			color: ROUTE_HEX,
			icon: STAGE_END_ICONS[end],
		})
	),
	POI_CLUSTER_MARKER,
	MAGIC_PENDING_MARKER,
	VARIANT_MARKER,
]

// Trazado SVG de la forma de fondo (círculo o cuadrado de esquinas redondeadas) de lado `size`
// con un margen `inset`.
export const shapePath = (shape: MarkerSpec['shape'], size: number, inset: number): string => {
	const a = inset
	const b = size - inset
	const side = b - a
	if (side <= 0) throw new RangeError('El margen no deja sitio a la forma')
	if (shape === 'circle') {
		const r = side / 2
		return `M${a} ${a + r}a${r} ${r} 0 1 0 ${side} 0a${r} ${r} 0 1 0 ${-side} 0Z`
	}
	const r = Math.min(7, side / 4)
	return `M${a + r} ${a}H${b - r}Q${b} ${a} ${b} ${a + r}V${b - r}Q${b} ${b} ${b - r} ${b}H${a + r}Q${a} ${b} ${a} ${b - r}V${a + r}Q${a} ${a} ${a + r} ${a}Z`
}

// Lo mínimo de un contexto 2D de canvas que usa el dibujo (los tests pasan uno falso).
export type MarkerContext = Pick<
	CanvasRenderingContext2D,
	'scale' | 'translate' | 'fill' | 'stroke' | 'fillStyle' | 'strokeStyle' | 'lineWidth' | 'lineCap' | 'lineJoin'
>
export type MakePath = (d: string) => Path2D

const BORDER = 2
const RING = 3 // grosor del aro de un marcador hueco
const ICON_SIZE = 18

// Dibuja el marcador: borde blanco, fondo de color e icono blanco centrado (si lleva). Hueco:
// borde blanco, aro del color, fondo blanco e icono del color.
export const drawMarker = (ctx: MarkerContext, spec: MarkerSpec, pixelRatio: number, makePath: MakePath) => {
	ctx.scale(pixelRatio, pixelRatio)
	ctx.fillStyle = '#ffffff'
	ctx.fill(makePath(shapePath(spec.shape, MARKER_SIZE, 1)))
	ctx.fillStyle = spec.color
	ctx.fill(makePath(shapePath(spec.shape, MARKER_SIZE, 1 + BORDER)))
	if (spec.outline) {
		ctx.fillStyle = '#ffffff'
		ctx.fill(makePath(shapePath(spec.shape, MARKER_SIZE, 1 + BORDER + RING)))
	}
	if (!spec.icon) return
	const offset = (MARKER_SIZE - ICON_SIZE) / 2
	ctx.translate(offset, offset)
	ctx.scale(ICON_SIZE / 24, ICON_SIZE / 24) // los iconos están en una rejilla de 24
	ctx.strokeStyle = spec.outline ? spec.color : '#ffffff'
	ctx.lineWidth = 2.25
	ctx.lineCap = 'round'
	ctx.lineJoin = 'round'
	ctx.stroke(makePath(ICON_PATHS[spec.icon]))
}

type CanvasLike = { width: number; height: number; getContext(type: '2d'): CanvasRenderingContext2D | null }

// Pinta el marcador en un canvas y devuelve sus píxeles para `map.addImage`; null si el
// navegador no da contexto 2D (el mapa sigue funcionando, solo sin ese icono).
export const rasterizeMarker = (
	spec: MarkerSpec,
	pixelRatio = MARKER_PIXEL_RATIO,
	createCanvas: () => CanvasLike = () => document.createElement('canvas'),
	makePath: MakePath = (d) => new Path2D(d)
): ImageData | null => {
	const canvas = createCanvas()
	const side = MARKER_SIZE * pixelRatio
	canvas.width = side
	canvas.height = side
	const ctx = canvas.getContext('2d')
	if (!ctx) return null
	drawMarker(ctx, spec, pixelRatio, makePath)
	return ctx.getImageData(0, 0, side, side)
}
